// src/utils/shareProduct.js
//
// Логика шаринга товара.
// - Готовит картинку (через productCardImage)
// - Составляет caption по шаблону (с подстановкой {name}, {price}, {description}, {shop}, {shopId})
// - Шарит универсально (Capacitor → Web Share → fallback)
// - Для Stories — пытается прямо открыть Instagram Stories

import { generateProductCardSquare, generateProductCardVertical } from './productCardImage';

const CAPTION_TEMPLATE_KEY = 'alsatuu:caption-template';

// ───────────────────────────────────────────────────────────────
// Шаблон caption
// ───────────────────────────────────────────────────────────────

export const DEFAULT_CAPTION_TEMPLATE =
  '{name}\n' +
  '{price}\n' +
  '\n' +
  '{description}\n' +
  '\n' +
  'Найди этот товар в Alsatuu →\n' +
  '📍 {shop} (ID: {shopId})';

export function getCaptionTemplate() {
  try {
    return localStorage.getItem(CAPTION_TEMPLATE_KEY) || DEFAULT_CAPTION_TEMPLATE;
  } catch (e) {
    return DEFAULT_CAPTION_TEMPLATE;
  }
}

export function saveCaptionTemplate(template) {
  try { localStorage.setItem(CAPTION_TEMPLATE_KEY, template); } catch (e) {}
}

export function resetCaptionTemplate() {
  try { localStorage.removeItem(CAPTION_TEMPLATE_KEY); } catch (e) {}
}

// Подставляет переменные. Если description пустой — убирает блок с пустой строкой.
export function fillCaption(template, vars) {
  let s = template;
  // Если описания нет — удаляем и пустую строку перед ним
  if (!vars.description) {
    s = s.replace(/\n*\{description\}\n*/g, '\n\n').replace(/\n{3,}/g, '\n\n');
  }
  for (const [k, v] of Object.entries(vars)) {
    s = s.split(`{${k}}`).join(String(v ?? ''));
  }
  return s.trim();
}

// ───────────────────────────────────────────────────────────────
// Capacitor helpers
// ───────────────────────────────────────────────────────────────

function loadCapacitor() {
  try {
    // eslint-disable-next-line
    const cap = require('@capacitor/core');
    // eslint-disable-next-line
    const share = require('@capacitor/share');
    // eslint-disable-next-line
    const fs = require('@capacitor/filesystem');
    return { Capacitor: cap.Capacitor, Share: share.Share, Filesystem: fs.Filesystem, Directory: fs.Directory };
  } catch (e) {
    return null;
  }
}

function base64ToBlob(b64, mime = 'image/png') {
  const bin = atob(b64);
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Универсальный буфер обмена. Работает и в браузере, и в Capacitor webview.
async function copyToClipboard(text) {
  if (!text) return false;
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {}
  // Fallback на старый execCommand
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.left = '-1000px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    return false;
  }
}

// ───────────────────────────────────────────────────────────────
// Универсальный share
// ───────────────────────────────────────────────────────────────

export async function shareProductCard({ product, shop, caption, currency, formatMoney }) {
  const photoUrl = pickProductPhotoUrl(product);
  const priceText = formatMoney
    ? formatMoney(product.salePrice || 0, currency)
    : `${product.salePrice || 0} ${currency || ''}`;

  const base64 = await generateProductCardSquare({
    photoUrl,
    name: product.name,
    price: priceText,
    shopName: shop?.name,
  });

  const filename = `alsatuu-${(product.name || 'product')
    .toLowerCase()
    .replace(/[^a-z0-9а-яё]+/gi, '-')
    .slice(0, 40) || 'product'}.png`;

  // 1. Capacitor — нативный share
  const cap = loadCapacitor();
  if (cap && cap.Capacitor && cap.Capacitor.isNativePlatform && cap.Capacitor.isNativePlatform()) {
    const { Share, Filesystem, Directory } = cap;
    // Кладём caption в буфер обмена (в Capacitor webview navigator.clipboard работает)
    await copyToClipboard(caption);
    const write = await Filesystem.writeFile({
      path: filename,
      data: base64,
      directory: Directory.Cache,
    });
    await Share.share({
      title: product.name,
      text: caption,
      url: write.uri,
      dialogTitle: 'Поделиться товаром',
    });
    return { ok: true, method: 'capacitor' };
  }

  // 2. Web Share API с файлом
  const blob = base64ToBlob(base64);
  const file = new File([blob], filename, { type: 'image/png' });
  if (typeof navigator !== 'undefined' && navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: product.name, text: caption });
      return { ok: true, method: 'web-share' };
    } catch (e) {
      if (e && e.name === 'AbortError') return { ok: false, cancelled: true };
    }
  }

  // 3. Fallback: скачиваем + копируем в буфер
  downloadBlob(blob, filename);
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try { await navigator.clipboard.writeText(caption); } catch (e) {}
  }
  return { ok: true, method: 'download-fallback' };
}

// ───────────────────────────────────────────────────────────────
// Share в Instagram — генерирует ВЕРТИКАЛЬНУЮ картинку 1080×1920
// для красивого отображения в Stories (без полос сверху/снизу).
// Capacitor Share не может таргетить конкретное приложение, поэтому
// открывается системный share-sheet — пользователь выбирает Instagram.
// ───────────────────────────────────────────────────────────────

export async function shareProductToStories({ product, shop, caption, currency, formatMoney }) {
  const photoUrl = pickProductPhotoUrl(product);
  const priceText = formatMoney
    ? formatMoney(product.salePrice || 0, currency)
    : `${product.salePrice || 0} ${currency || ''}`;

  const base64 = await generateProductCardVertical({
    photoUrl,
    name: product.name,
    price: priceText,
    shopName: shop?.name,
  });

  const cap = loadCapacitor();
  if (!cap || !cap.Capacitor || !cap.Capacitor.isNativePlatform || !cap.Capacitor.isNativePlatform()) {
    // В вебе deep-link Instagram не работает — делаем обычный share
    return shareProductCard({ product, shop, caption, currency, formatMoney });
  }

  // На нативе: пишем файл, копируем caption в буфер, открываем Instagram через share
  const { Share, Filesystem, Directory } = cap;
  const filename = `alsatuu-story-${Date.now()}.png`;

  // Кладём caption в буфер обмена (Instagram Stories не принимает текст через share —
  // только через ручную вставку)
  await copyToClipboard(caption);

  const write = await Filesystem.writeFile({
    path: filename,
    data: base64,
    directory: Directory.Cache,
  });

  // Capacitor Share не умеет таргетить конкретное приложение —
  // пользователь сам выберет Instagram в системном share-sheet.
  await Share.share({
    title: 'В Instagram',
    text: caption,
    url: write.uri,
    dialogTitle: 'Открыть в Instagram',
  });

  return { ok: true, method: 'capacitor-instagram' };
}

// ───────────────────────────────────────────────────────────────
// Утилиты
// ───────────────────────────────────────────────────────────────

// Возвращает URL основного фото товара (или null).
// Бэк уже возвращает абсолютные URL, поэтому никаких префиксов не строим —
// просто берём первое доступное фото.
export function pickProductPhotoUrl(product) {
  if (!product) return null;
  if (product.photo) return product.photo;
  if (Array.isArray(product.photos) && product.photos.length > 0) return product.photos[0];
  if (typeof product.photos === 'string') {
    try {
      const arr = JSON.parse(product.photos);
      if (Array.isArray(arr) && arr[0]) return arr[0];
    } catch (e) {}
  }
  return null;
}
